import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, mensajeError } from '../../lib/api';
import { Button } from '../ui/button';
import { AlertCircle, Bot, Loader2, RefreshCw } from 'lucide-react';
import { FOCO, Seccion, claveUsable, comoObjeto, cuando, numero, unir } from './bot/comun';
import ClaveBot from './bot/ClaveBot';
import AjustesBot from './bot/AjustesBot';
import InstruccionesBot from './bot/InstruccionesBot';
import SimuladorBot from './bot/SimuladorBot';
import PlantillasBot from './bot/PlantillasBot';
import PruebasBot, { SONDEO_MS } from './bot/PruebasBot';
import MetricasBot from './bot/MetricasBot';

/**
 * WhatsApp → «Bot» (1/11/2026, solo administradores). El asistente virtual
 * que contesta a los leads por WhatsApp, dentro de ClinicPilot (opción A).
 * En este orden:
 *  1. Estado: apagado / modo prueba (solo borradores) / enviando, y qué
 *     falta para el siguiente paso.
 *  2. La clave de la API de Claude (se pega, no se vuelve a ver nunca).
 *  3. Ajustes: modelo, esfuerzo, ritmo humano, enlace de cita, avisos de
 *     traspaso y precios por millón de tokens.
 *  4. Las instrucciones (prompt), con su historial de versiones.
 *  5. «Probar el bot»: un simulador que no envía nada.
 *  6. Las plantillas que salen fuera de la ventana de 24 h, para mandarlas a
 *     aprobación, y los seguimientos automáticos.
 *  7. Pruebas automáticas con los casos que sube dirección.
 *  8. Métricas.
 * Ningún dato de la clínica está en el código: teléfonos, nombres, textos,
 * plantillas y precios llegan del servidor y se rellenan aquí.
 */

const MODOS = {
  apagado: {
    etiqueta: 'Apagado',
    clase: 'bg-zinc-100 text-zinc-700 ring-zinc-300',
    explica: 'No contesta ni prepara borradores.',
  },
  prueba: {
    etiqueta: 'Modo prueba: solo borradores',
    clase: 'bg-amber-100 text-amber-900 ring-amber-300',
    explica: 'Prepara un borrador para cada mensaje que entra en las conversaciones que lleva el bot. No envía nada ni toca los leads.',
  },
  envia: {
    etiqueta: 'Enviando',
    clase: 'bg-emerald-100 text-emerald-800 ring-emerald-300',
    explica: 'Contesta por WhatsApp en las conversaciones que lleva el bot, escribe en el lead y pasa al equipo cuando toca.',
  },
};

const SECCIONES = [
  ['estado', 'Estado'],
  ['clave', 'Clave'],
  ['ajustes', 'Ajustes'],
  ['instrucciones', 'Instrucciones'],
  ['probar', 'Probar'],
  ['plantillas', 'Plantillas'],
  ['pruebas', 'Pruebas'],
  ['metricas', 'Métricas'],
];

/** Lo que falta para pasar al siguiente modo, en una frase. */
export function queFalta(bot) {
  const modo = bot.modo || 'apagado';
  if (modo === 'apagado') {
    const f = [];
    if (bot.clave_ilegible) f.push('volver a pegar la clave de la API de Claude (la guardada no se puede leer)');
    else if (!claveUsable(bot)) f.push('pegar la clave de la API de Claude');
    if (!bot.prompt) f.push('guardar las instrucciones');
    return f.length ? `Para pasar a modo prueba falta ${unir(f)}.` : 'Revisa la clave y las instrucciones.';
  }
  if (modo === 'prueba') {
    const f = [];
    if (!bot.bot_activo) f.push('encender «El bot puede contestar»');
    if (!bot.conectado) f.push('conectar WhatsApp');
    return f.length ? `Para que envíe falta ${unir(f)} (en la pestaña Conexión).` : 'Revisa la conexión de WhatsApp.';
  }
  return 'Para pararlo, apaga «El bot puede contestar» en Conexión: vuelve a preparar solo borradores.';
}

/** Lo que miran los ajustes: si cambia en el servidor, el formulario se rehace. */
function firmaAjustes(bot) {
  return JSON.stringify([bot.modelo, bot.modelos_sugeridos, bot.esfuerzo, bot.esfuerzos, bot.ritmo_humano,
    bot.telefono_cita, bot.texto_cita, bot.avisar_a, bot.personas_posibles, bot.avisar_whatsapp, bot.precios,
    bot.lead_nuevo, bot.marcas_posibles, bot.tope_respuestas_dia, bot.tope_llamadas_dia]);
}

function EstadoDelBot({ bot, onIrAConexion }) {
  const modo = MODOS[bot.modo] ? bot.modo : 'apagado';
  const m = MODOS[modo];
  const uso = comoObjeto(bot.uso_30_dias);
  const error = comoObjeto(bot.ultimo_error);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span data-testid="bot-modo" data-modo={modo}
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-sm font-semibold ring-1 ring-inset ${m.clase}`}>
          <Bot className="w-4 h-4" aria-hidden="true" /> {m.etiqueta}
        </span>
        <span className="text-sm text-muted-foreground">{m.explica}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-[#13231E]" data-testid="bot-que-falta">{queFalta(bot)}</span>
        {modo !== 'apagado' && onIrAConexion ? (
          <Button type="button" variant="link" size="sm" className="h-auto px-0" onClick={onIrAConexion}
            data-testid="bot-ir-conexion">
            Ir a Conexión
          </Button>
        ) : null}
      </div>
      {error.motivo ? (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs text-red-800"
          data-testid="bot-ultimo-error">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden="true" />
          <span>Último error{error.at ? ` (${cuando(error.at)})` : ''}: {error.motivo}</span>
        </div>
      ) : null}
      {uso.llamadas !== undefined ? (
        <p className="text-xs text-muted-foreground tabular-nums" data-testid="bot-uso-30">
          Últimos 30 días: {numero(uso.llamadas)} llamadas a Claude · tokens: {numero(uso.tokens_entrada)} de entrada,{' '}
          {numero(uso.tokens_cache_leidos)} leídos de caché, {numero(uso.tokens_salida)} de salida.
        </p>
      ) : null}
    </div>
  );
}

function irA(id) {
  const el = document.getElementById(`bot-${id}`);
  if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const titulo = document.getElementById(`bot-${id}-titulo`);
  if (titulo) {
    titulo.setAttribute('tabindex', '-1');
    titulo.focus({ preventScroll: true });
  }
}

export default function BotWhatsApp({ onIrAConexion, sondeoMs = SONDEO_MS }) {
  const [bot, setBot] = useState(null);
  const [fallo, setFallo] = useState('');
  const [textoEditor, setTextoEditor] = useState('');
  const [plantillas, setPlantillas] = useState(null);
  const [falloPlantillas, setFalloPlantillas] = useState('');
  // La versión cuyo texto está en el editor: si llega otra (guardar,
  // restaurar), el editor pasa a la nueva; si no, se respeta lo escrito.
  const versionEditorRef = useRef(undefined);

  const aplicar = useCallback((datos) => {
    const d = comoObjeto(datos);
    setBot(d);
    const v = d.prompt ? d.prompt.version : null;
    if (v !== versionEditorRef.current) {
      versionEditorRef.current = v;
      setTextoEditor(d.prompt?.texto || '');
    }
  }, []);

  const cargar = useCallback(async () => {
    try {
      const r = await api.get('/api/whatsapp/bot');
      aplicar(r.data);
      setFallo('');
    } catch (e) {
      setFallo(mensajeError(e, 'No se ha podido leer el bot'));
    }
  }, [aplicar]);

  const cargarPlantillas = useCallback(async () => {
    try {
      const r = await api.get('/api/whatsapp/bot/plantillas');
      setPlantillas(comoObjeto(r.data));
      setFalloPlantillas('');
    } catch (e) {
      setFalloPlantillas(mensajeError(e, 'No se han podido leer las plantillas del bot'));
    }
  }, []);

  useEffect(() => { cargar(); cargarPlantillas(); }, [cargar, cargarPlantillas]);

  if (fallo && !bot) {
    return (
      <div className="text-sm text-red-700 border border-red-200 bg-red-50 rounded p-3 flex flex-wrap items-center gap-2"
        data-testid="bot-fallo">
        <span className="flex-1 min-w-0">{fallo}</span>
        <Button type="button" size="sm" variant="outline" onClick={cargar}>
          <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" /> Reintentar
        </Button>
      </div>
    );
  }
  if (!bot) {
    return (
      <div className="p-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Cargando el bot…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="bot-whatsapp">
      <nav aria-label="Secciones del bot" className="flex flex-wrap gap-1.5">
        {SECCIONES.map(([id, nombre], i) => (
          <a key={id} href={`#bot-${id}`} onClick={(e) => { e.preventDefault(); irA(id); }}
            className={`rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/60 ${FOCO}`}>
            {i + 1}. {nombre}
          </a>
        ))}
      </nav>

      <Seccion id="estado" n={1} titulo="Estado">
        <EstadoDelBot bot={bot} onIrAConexion={onIrAConexion} />
      </Seccion>

      <Seccion id="clave" n={2} titulo="Clave de la API de Claude">
        <ClaveBot bot={bot} onCambio={aplicar} />
      </Seccion>

      <Seccion id="ajustes" n={3} titulo="Ajustes">
        <AjustesBot key={firmaAjustes(bot)} bot={bot} onCambio={aplicar} />
      </Seccion>

      <Seccion id="instrucciones" n={4} titulo="Instrucciones"
        descripcion="Lo que el bot sabe y cómo tiene que hablar. Cada vez que se guarda queda una versión nueva; las anteriores se pueden leer y restaurar.">
        <InstruccionesBot bot={bot} texto={textoEditor} onTexto={setTextoEditor} onCambio={aplicar} />
      </Seccion>

      <Seccion id="probar" n={5} titulo="Probar el bot"
        descripcion="Escribe como si fueras un contacto y mira qué contestaría. No se envía nada ni se toca ningún lead.">
        <SimuladorBot bot={bot} textoEditor={textoEditor} plantillas={plantillas} />
      </Seccion>

      <Seccion id="plantillas" n={6} titulo="Plantillas"
        descripcion="Los mensajes que salen fuera de la ventana de 24 h. Meta tiene que aprobarlos antes de usarlos.">
        <PlantillasBot datos={plantillas} fallo={falloPlantillas} onCambio={(d) => setPlantillas(comoObjeto(d))}
          onRecargar={cargarPlantillas} />
      </Seccion>

      <Seccion id="pruebas" n={7} titulo="Pruebas automáticas"
        descripcion="Conversaciones de prueba que se pasan contra Claude en el servidor, con lo que debe cumplir cada respuesta.">
        <PruebasBot bot={bot} sondeoMs={sondeoMs} />
      </Seccion>

      <Seccion id="metricas" n={8} titulo="Métricas"
        descripcion="Solo lo que el bot ha enviado de verdad; los borradores, el simulador y las pruebas cuentan aparte en el uso.">
        <MetricasBot />
      </Seccion>
    </div>
  );
}
